import { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import Header from "../../../components/Header";
import LoadingOverlay from "../../../components/LoadingOverlay";
import ReportFiltersCard, {
    ReportFilterField,
} from "../../../components/reports/ReportFiltersCard";
import Sidebar from "../../../components/Sidebar";
import { usePermissions } from "../../../context/PermissionsContext";
import { getInactiveReport } from "../../../services/analytics/getInactiveReport";
import { getManagers } from "../../../services/employees/getManagers";
import InactiveClientsTable from "./components/InactiveClientsTable";
import InactiveReportTable from "./components/InactiveReportTable";
import {
    ALL_TEAM_LEADERS_OPTION,
    DEFAULT_INACTIVITY_PERIOD,
    INACTIVITY_OPTIONS,
    buildTeamLeaderOptions,
} from "./inactiveReportUtils";

const getInitialFilters = () => ({
    inactivityPeriod: DEFAULT_INACTIVITY_PERIOD,
    teamLeaderId: ALL_TEAM_LEADERS_OPTION.value,
});

const REPORT_VIEWS = Object.freeze({
    clients: "Clients",
    services: "Services",
});

const getResponseRows = (response, view) => {
    const payload = response?.data;
    const rows = view === "clients"
        ? payload?.inactiveClients ??
          payload?.clients ??
          payload?.items ??
          payload?.report?.clients ??
          payload?.data
        : payload?.inactiveServices ??
          payload?.services ??
          payload?.items ??
          payload?.report?.services ??
          payload?.data;

    if (Array.isArray(rows)) return rows;
    return Array.isArray(payload) ? payload : [];
};

const getResponseMessage = (response, fallback) =>
    response?.message ??
    response?.data?.message ??
    response?.data?.Message ??
    fallback;

const getTeamLeaders = (response) => {
    const payload = response?.data;
    const teamLeaders =
        payload?.managers ??
        payload?.Managers ??
        payload?.teamLeaders ??
        payload?.TeamLeaders;

    return Array.isArray(teamLeaders) ? teamLeaders : null;
};

const InactiveReport = () => {
    const { can, permissionsLoading } = usePermissions();
    const canReadServices = can("client_services.read");
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [activeView, setActiveView] = useState("clients");
    const [filters, setFilters] = useState(getInitialFilters);
    const [appliedFilters, setAppliedFilters] = useState(getInitialFilters);
    const [teamLeaderOptions, setTeamLeaderOptions] = useState([
        ALL_TEAM_LEADERS_OPTION,
    ]);
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [areTeamLeadersLoading, setAreTeamLeadersLoading] = useState(true);
    const [reportError, setReportError] = useState("");
    const [teamLeaderError, setTeamLeaderError] = useState("");
    const [tableResetSignal, setTableResetSignal] = useState(0);
    const resultsSectionRef = useRef(null);
    const shouldScrollToResultsRef = useRef(false);

    const selectedInactivityOption = useMemo(
        () =>
            INACTIVITY_OPTIONS.find(
                (option) => option.value === filters.inactivityPeriod,
            ) ??
            INACTIVITY_OPTIONS.find(
                (option) => option.value === DEFAULT_INACTIVITY_PERIOD,
            ),
        [filters.inactivityPeriod],
    );

    const selectedTeamLeaderOption = useMemo(
        () =>
            teamLeaderOptions.find(
                (option) => option.value === filters.teamLeaderId,
            ) ?? ALL_TEAM_LEADERS_OPTION,
        [filters.teamLeaderId, teamLeaderOptions],
    );

    useEffect(() => {
        let isMounted = true;

        const loadTeamLeaders = async () => {
            if (permissionsLoading) return;

            if (!canReadServices) {
                setAreTeamLeadersLoading(false);
                return;
            }

            setAreTeamLeadersLoading(true);
            setTeamLeaderError("");

            try {
                const response = await getManagers();

                if (!isMounted) return;

                const teamLeaders = getTeamLeaders(response);
                if (
                    !response?.success ||
                    response?.data?.success === false ||
                    teamLeaders === null
                ) {
                    setTeamLeaderOptions([ALL_TEAM_LEADERS_OPTION]);
                    setTeamLeaderError(
                        getResponseMessage(
                            response,
                            "Unable to load Team Leaders.",
                        ),
                    );
                    return;
                }

                const leaderOptions = buildTeamLeaderOptions(teamLeaders);
                setTeamLeaderOptions([
                    ALL_TEAM_LEADERS_OPTION,
                    ...leaderOptions,
                ]);
            } catch (error) {
                if (!isMounted) return;
                setTeamLeaderOptions([ALL_TEAM_LEADERS_OPTION]);
                setTeamLeaderError(
                    error?.response?.data?.message ??
                        error?.message ??
                        "Unable to load Team Leaders.",
                );
            } finally {
                if (isMounted) setAreTeamLeadersLoading(false);
            }
        };

        loadTeamLeaders();

        return () => {
            isMounted = false;
        };
    }, [canReadServices, permissionsLoading]);

    useEffect(() => {
        let isMounted = true;

        const loadReport = async () => {
            if (permissionsLoading) return;

            if (!canReadServices) {
                setRows([]);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setReportError("");

            try {
                const response = await getInactiveReport({
                    view: REPORT_VIEWS[activeView],
                    inactivityPeriod: appliedFilters.inactivityPeriod,
                    teamLeaderId: appliedFilters.teamLeaderId || null,
                });

                if (!isMounted) return;

                if (!response?.success || response?.data?.success === false) {
                    setRows([]);
                    setReportError(
                        getResponseMessage(
                            response,
                            "Error loading Inactive Report.",
                        ),
                    );
                    return;
                }

                setRows(getResponseRows(response, activeView));
            } catch (error) {
                if (!isMounted) return;
                setRows([]);
                setReportError(
                    error?.response?.data?.message ??
                        error?.message ??
                        "Error loading Inactive Report.",
                );
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        loadReport();

        return () => {
            isMounted = false;
        };
    }, [activeView, appliedFilters, canReadServices, permissionsLoading]);

    useEffect(() => {
        if (isLoading || !shouldScrollToResultsRef.current) return;

        resultsSectionRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
        shouldScrollToResultsRef.current = false;
    }, [isLoading, rows]);

    const handleApplyFilters = () => {
        shouldScrollToResultsRef.current = true;
        setTableResetSignal((current) => current + 1);
        setAppliedFilters({ ...filters });
    };

    const handleResetFilters = () => {
        const initialFilters = getInitialFilters();
        setFilters(initialFilters);
        shouldScrollToResultsRef.current = true;
        setTableResetSignal((current) => current + 1);
        setAppliedFilters({ ...initialFilters });
    };

    const handleViewChange = (view) => {
        if (view === activeView) return;
        setRows([]);
        setReportError("");
        setTableResetSignal((current) => current + 1);
        setActiveView(view);
    };

    const error = reportError || teamLeaderError;
    const overlayIsLoading = permissionsLoading || isLoading;

    return (
        <>
            <LoadingOverlay isLoading={overlayIsLoading} />
            <div className="flex h-screen overflow-hidden">
                <Sidebar
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                />

                <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                    <Header
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                    />

                    <main>
                        <div className="w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                            <h1 className="text-2xl font-bold md:text-3xl text-slate-800">
                                Inactive Report
                            </h1>

                            {!permissionsLoading && !canReadServices ? (
                                <div className="p-4 mt-6 text-sm bg-white border rounded-sm text-slate-500 border-slate-200">
                                    You don't have permission to view inactive
                                    report data.
                                </div>
                            ) : (
                                <>
                                    <div
                                        className="inline-flex p-1 mt-6 bg-slate-100 rounded-lg"
                                        role="tablist"
                                        aria-label="Inactive report view"
                                    >
                                        {Object.keys(REPORT_VIEWS).map((view) => (
                                            <button
                                                key={view}
                                                type="button"
                                                role="tab"
                                                aria-selected={activeView === view}
                                                onClick={() => handleViewChange(view)}
                                                disabled={overlayIsLoading}
                                                className={`px-4 py-2 text-sm font-medium rounded-md transition focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60 ${
                                                    activeView === view
                                                        ? "text-white bg-teal-500 shadow-sm hover:bg-teal-600"
                                                        : "text-slate-600 hover:bg-white hover:text-slate-800"
                                                }`}
                                            >
                                                {REPORT_VIEWS[view]}
                                            </button>
                                        ))}
                                    </div>

                                    <ReportFiltersCard
                                        title="Filters"
                                        actions={
                                            <div className="flex flex-wrap items-center justify-end gap-2">
                                                <button
                                                    type="button"
                                                    onClick={handleResetFilters}
                                                    disabled={overlayIsLoading}
                                                    className="inline-flex items-center px-4 py-2 text-sm font-medium transition bg-white border rounded-lg shadow-sm text-slate-700 border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                                                >
                                                    Reset
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleApplyFilters}
                                                    disabled={overlayIsLoading}
                                                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white transition bg-teal-500 rounded-lg shadow-sm hover:bg-teal-600 disabled:cursor-not-allowed disabled:opacity-60"
                                                >
                                                    Apply Filters
                                                </button>
                                            </div>
                                        }
                                        fieldsClassName="grid grid-cols-1 gap-4 md:grid-cols-2"
                                    >
                                        <ReportFilterField label="Inactive For">
                                            <Select
                                                name="inactiveFor"
                                                value={selectedInactivityOption}
                                                onChange={(option) =>
                                                    setFilters((current) => ({
                                                        ...current,
                                                        inactivityPeriod:
                                                            option?.value ??
                                                            DEFAULT_INACTIVITY_PERIOD,
                                                    }))
                                                }
                                                options={INACTIVITY_OPTIONS}
                                                classNamePrefix="select"
                                                isSearchable={false}
                                                isDisabled={overlayIsLoading}
                                            />
                                        </ReportFilterField>

                                        <ReportFilterField label="Team Leader">
                                            <Select
                                                name="teamLeader"
                                                value={selectedTeamLeaderOption}
                                                onChange={(option) =>
                                                    setFilters((current) => ({
                                                        ...current,
                                                        teamLeaderId:
                                                            option?.value ?? "",
                                                    }))
                                                }
                                                options={teamLeaderOptions}
                                                classNamePrefix="select"
                                                isSearchable
                                                isLoading={areTeamLeadersLoading}
                                                isDisabled={overlayIsLoading}
                                            />
                                        </ReportFilterField>

                                    </ReportFiltersCard>

                                    {error && (
                                        <div className="p-3 mt-6 text-sm text-red-700 border border-red-200 rounded-lg shadow-sm bg-red-50">
                                            {error}
                                        </div>
                                    )}

                                    <div ref={resultsSectionRef} className="mt-6">
                                        {!isLoading && !reportError && rows.length > 0 && (
                                            activeView === "clients" ? (
                                                <InactiveClientsTable
                                                    data={rows}
                                                    resetPageSignal={tableResetSignal}
                                                />
                                            ) : (
                                                <InactiveReportTable
                                                    data={rows}
                                                    resetPageSignal={tableResetSignal}
                                                />
                                            )
                                        )}

                                        {!isLoading && !reportError && rows.length === 0 && (
                                            <p className="text-sm font-medium text-slate-500">
                                                No inactive {activeView} found for the
                                                selected filters.
                                            </p>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </main>
                </div>
            </div>
        </>
    );
};

export default InactiveReport;
